/**
 * El respaldo de una declaración de consentimiento y lo que cambia declararla (decisión de Alan del 29/10/2026), en
 * palabras. Puro, salvo `leerRespaldo` (el `FileReader` del navegador).
 *
 * - El respaldo es la captura o el PDF del mail o del chat donde el comercio confirmó que sus clientes aceptaron. Se
 *   revisa acá ANTES de leerlo (el tipo y el tamaño, como `wa-oficial/logic/archivo.ts`) y el backend lo vuelve a
 *   revisar por los bytes (`base-ventas/util/respaldo.util.ts`): lo de acá es para decirlo antes, no la defensa.
 * - A cuántos les vuelve «sí»: los mismos números que cuenta el backend con el criterio del envío.
 */

import { num, plural } from "../../campana-nueva/utils/format";
import type { AlcanceDeLaDeclaracion, RespaldoParaSubir } from "../types";

export const MAX_BYTES_RESPALDO = 5 * 1024 * 1024;
/** Lo que ofrece el selector de archivos: una captura (JPG o PNG) o un PDF. */
export const ACEPTA_RESPALDO = "image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf";

export const TEXTO_TIPO_RESPALDO = "El respaldo tiene que ser una imagen (JPG o PNG) o un PDF.";
export const TEXTO_RESPALDO_GRANDE = "El respaldo pesa más de 5 MB: achicá la captura o mandá sólo la página que lo muestra.";

const TIPOS = new Set(["image/jpeg", "image/png", "application/pdf"]);
const EXTENSIONES = /\.(jpe?g|png|pdf)$/i;

/** "" si sirve; si no, qué pasa. Por el tipo que dice el navegador o, si no dice nada, por la extensión. */
export function problemaDelRespaldo(f: { name?: string; size?: number; type?: string }): string {
  const tipo = String(f.type ?? "").toLowerCase();
  const sirve = tipo ? TIPOS.has(tipo) : EXTENSIONES.test(String(f.name ?? ""));
  if (!sirve) return TEXTO_TIPO_RESPALDO;
  if ((f.size ?? 0) > MAX_BYTES_RESPALDO) return TEXTO_RESPALDO_GRANDE;
  if (!f.size) return "Ese archivo está vacío: elegí la captura o el PDF de nuevo.";
  return "";
}

/** El respaldo como lo espera el backend: el nombre y el contenido en base64 (con el prefijo `data:`, que el backend saca). */
export function leerRespaldo(f: File): Promise<RespaldoParaSubir> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve({ nombreArchivo: f.name, contenido: typeof r.result === "string" ? r.result : "" });
    r.onerror = () => reject(new Error("No pudimos leer el respaldo. Probá de nuevo o elegilo otra vez."));
    r.readAsDataURL(f);
  });
}

/** «1,2 MB», «340 KB». */
export function pesoDelArchivo(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toLocaleString("es-AR", { maximumFractionDigits: 1 })} MB`;
  return `${Math.max(1, Math.round(bytes / 1024)).toLocaleString("es-AR")} KB`;
}

/** Lo que dice la pantalla de a cuántos les vuelve «sí»: el titular y, en renglones, a quiénes no los toca y por qué. */
export function textosAlcance(a: AlcanceDeLaDeclaracion): { titular: string; detalle: string[] } {
  const titular =
    a.vuelvenSi === 0
      ? "No le cambia nada a nadie: no hay clientes sin decir si aceptaron que se les pueda escribir."
      : `Vuelve «sí» a ${num(a.vuelvenSi)} ${plural(a.vuelvenSi, "cliente", "clientes")} que el archivo no decía si aceptaron` +
        (a.vuelvenSiConCelular !== a.vuelvenSi ? ` (${num(a.vuelvenSiConCelular)} con celular, a los que les puede escribir WhatsApp oficial).` : ".");
  const detalle: string[] = [];
  if (a.yaDecianQueSi) detalle.push(`${num(a.yaDecianQueSi)} ya ${plural(a.yaDecianQueSi, "decía", "decían")} que sí en el archivo: no les cambia nada.`);
  if (a.afuera.dijo_que_no) detalle.push(`${num(a.afuera.dijo_que_no)} ${plural(a.afuera.dijo_que_no, "dijo", "dijeron")} que no en el archivo: quedan afuera igual.`);
  if (a.afuera.baja) detalle.push(`${num(a.afuera.baja)} ${plural(a.afuera.baja, "pidió", "pidieron")} la baja: quedan afuera igual.`);
  if (a.afuera.sin_contacto) detalle.push(`${num(a.afuera.sin_contacto)} sin celular ni mail: no hay a dónde escribirles.`);
  return { titular, detalle };
}
